import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/authContext";
import createAxiosInstance from "../../../utils/axiosConfig";
import {
	category_score,
	export_data,
	framework_domains,
	question_flows,
	score_history_category,
	user_flow_scores,
} from "../../../utils/endpoints";
import { FlowScore, FrameworkCategory, FrameworkDomain, QuestionFlow } from "../../../utils/interfaces";
import Domains from "./domains";
import ScoreHistory from "./sideScreen/scoreHistory";
import AdminTables from "../adminTables";
import ScoreHistoryIcon from "../../../assets/svg/scoreHistoryIcon";
import QuestionsFlow from "./questionsFlow";
import BulbIcon from "../../../assets/svg/ideaBulb";
import AddFrameworkModal from "../modal/addFrameworkModal";
import Alert from "../alerts/alert";
import FrameworkSetupSidescreen from "../frameworkSetupSidescreen";
import ScoringOptionsEditSidebar from "../scoringOptionsEditSidebar";
import FileSaver from "file-saver";


interface CategoryScore {
	category_id: number;
	total_score: number;
}

interface ScoreHistoryEntry {
	date: string;
	data: Array<{
		category_id?: number;
		category_title?: string;
		domain_id?: number;
		domain_title?: string;
		total_score: number;
		active_domains_count?: number;
		completed_flows_count?: number;
	}>;
}

const MaturityAssessment = ({
	frameworks,
	onFrameworksChange,
}: {
	frameworks: FrameworkCategory[];
	onFrameworksChange?: () => Promise<void>;
}) => {
	const { userId, userData, logout } = useAuth();
	const isFreeTier = userData?.account_type === 0;
	const navigate = useNavigate();
	const [selectedFramework, setSelectedFramework] =
		useState<FrameworkCategory | null>(null);
	const [domains, setDomains] = useState<FrameworkDomain[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isContentLoading, setIsContentLoading] = useState<boolean>(false);
	const [isQuestionsLoading, setIsQuestionsLoading] = useState<boolean>(false);
	const [categoryScore, setCategoryScore] = useState<CategoryScore[] | null>(null);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [questionFlows, setQuestionFlows] = useState<QuestionFlow[]>([]);
	const [flowScores, setFlowScores] = useState<FlowScore[]>([]);
	const [latestScores, setLatestScores] = useState<FlowScore[]>([]);
	const [selectedDomain, setSelectedDomain] = useState<FrameworkDomain>();
	const [refresh, setRefresh] = useState<number>(0);
	const [historyLoading, setHistoryLoading] = useState<boolean>(false);
	const [historyContent, setHistoryContent] = useState<ScoreHistoryEntry[]>([]);
	const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
	const [showAddFrameworkModal, setShowAddFrameworkModal] = useState<boolean>(false);
	const [alert, setAlert] = useState<{ message: string; type: 'success' | 'danger' | 'warning'; show: boolean } | null>(null);
	const [isEditMode, setIsEditMode] = useState<boolean>(false);
	const [editFrameworkData, setEditFrameworkData] = useState<FrameworkCategory | null>(null);
	const [showFrameworkSetup, setShowFrameworkSetup] = useState<boolean>(false);
	const [showScoringOptionsEdit, setShowScoringOptionsEdit] = useState(false);
	const [editScoringOptionsFrameworkId, setEditScoringOptionsFrameworkId] = useState<number | null>(null);
	const [newFrameworkData, setNewFrameworkData] = useState<FrameworkCategory | null>(null);
	const [isFramework, setIsFramework] = useState<boolean>(false);
    const [historyFramework, setHistoryFramework] = useState<number>(0);
	// Styles
	const cardStyle = {
		minHeight: '85vh',
		maxHeight: '85vh',
		padding: "10px"
	};

	const headerStyle = {
		borderBottom: "2px solid #5376F0",
		marginBottom: "0px",
		backgroundColor: "white"
	};

	const frameworksListStyle = {
		borderRight: "2px solid #5376F0",
		maxHeight: "75vh", // Fixed height to ensure scrolling works
		overflow: "auto" as const,
		flex: 1,
	};

	const sectionStyle = {
		borderRight: "2px solid #5376F0",
		maxHeight: "70vh",
		overflow: "auto" as const,
		paddingLeft: "10px",
		flex: 1,
	};

	const lastSectionStyle = {
		maxHeight: "70vh",
		overflow: "auto" as const,
		paddingLeft: "10px",
		flex: 1,
	};

	const handleHistoryClose = () => {
		setIsOpen(false);
	};

	const handleHistoryOpen = (categoryId: number) => {
		setIsOpen(true);
		setHistoryLoading(true);
		setIsFramework(true);
		fetchScoreHistory(categoryId);
	};

	const handleAuthError = () => {
		console.log("User token expired");
		logout();
		navigate("/");
	};

	const fetchScoreHistory = async (categoryId: number) => {
		setHistoryLoading(true);
		try {
			const scoreHistory = await createAxiosInstance().get(
				score_history_category(userId || 0, categoryId)
			);
			
			if (scoreHistory.status === 401 || scoreHistory.status === 403) {
				handleAuthError();
				return;
			}

		setHistoryContent(scoreHistory?.data || []);
		} catch (error) {
			console.error("Error fetching score history:", error);
		} finally {
			setHistoryLoading(false);
		}
	};

	const fetchCategoryScore = async () => {
		if (!userId) return;
		
		try {
			setIsLoading(true);
			const response = await createAxiosInstance().get(
				category_score(userId)
			);
			
			if (response.status === 401 || response.status === 403) {
				handleAuthError();
				return;
			}
			
			console.log("Category scores fetched:", response?.data);
			setCategoryScore(response?.data || []);
		} catch (error) {
			console.error("Error fetching category score:", error);
			throw error;
		} finally {
			setIsLoading(false);
		}
	};

	const findScoreForFramework = (frameworkId: number) => {
		return categoryScore?.find(
			(score: CategoryScore) => score.category_id === frameworkId
		);
	};

	// Filter out deleted frameworks and then sort by is_enabled
	const sortedFrameworks = frameworks
		.filter(framework => framework.is_deleted !== true)
		.sort(
			(a: FrameworkCategory, b: FrameworkCategory) =>
				(b.is_enabled ? 1 : 0) - (a.is_enabled ? 1 : 0)
		);

	const handleFrameworkSelect = (framework: FrameworkCategory) => {
		if (framework.is_enabled) {
			setSelectedFramework(framework);
			// Persist selection with user context
			if (userId) {
				localStorage.setItem(`maturity_selectedFrameworkId_${userId}`, framework.id.toString());
			}
			// Clear question flow related data when switching frameworks
			setSelectedDomain(undefined);
			setQuestionFlows([]);
			setFlowScores([]);
			setLatestScores([]);
			
			if (framework) {
				fetchDomains(framework.id); // Auto-select first domain via useEffect
			}
		}
	};

	const headerTextStyle = {
		color: "#183FCE",
		fontSize: '12px',
		fontWeight: 'bold' as const
	};
	
	const tableColumns = [
		{
			accessorKey: "title",
			header: () => <span style={headerTextStyle}>Assessment Name</span>,
			width: (userData?.is_admin === 1 || userData?.account_type === 5) ? "40%" : "45%",
			cell: ({ row }: { row: any }) => {
				const framework = row.original;
				const isSelected = selectedFramework?.id === framework.id;
				const isDisabled = !framework.is_enabled;
				const isFirstFramework = sortedFrameworks.indexOf(framework) === 0;
				const isRestrictedForFreeTier = isFreeTier && !isFirstFramework;
				
				return (
					<span style={{
						color: isSelected ? "#FFFFFF" : (isDisabled || isRestrictedForFreeTier ? "#B0B0B0" : "#2A2A2A"), 
						fontSize: '16px'
					}}>
						{framework.title}
					</span>
				);
			},
		},
		{
			id: "maturity_score",
			header: () => <span style={headerTextStyle}>Current Maturity Score</span>,
			width: (userData?.is_admin === 1 || userData?.account_type === 5) ? "40%" : "45%",
			cell: ({ row }: { row: any }) => {
				const framework = row.original;
				const scores = findScoreForFramework(framework.id);
				const displayScore = scores?.total_score ?? 0;
				const hasScore = scores && scores.total_score > 0;
				const isSelected = selectedFramework?.id === framework.id;
				const isDisabled = !framework.is_enabled;
				const isFirstFramework = sortedFrameworks.indexOf(framework) === 0;
				const isRestrictedForFreeTier = isFreeTier && !isFirstFramework;
				
				return (
					<span style={{
						color: isSelected ? "#FFFFFF" : (isDisabled || isRestrictedForFreeTier ? "#B0B0B0" : (hasScore ? "#5376F0" : "#808080")), 
						fontSize: '16px', 
						fontWeight: 'bold',
						textAlign: 'center'
					}}>
						{displayScore > 0 ? `${displayScore.toFixed(2)}` : "0.00"}
					</span>
				);
			},
		},
		{
			id: "history",
			header: () => <span style={headerTextStyle}>History</span>,
			width: "10%",
			cell: ({ row }: { row: any }) => {
				const framework = row.original;
				const scores = findScoreForFramework(framework.id);
				const hasScore = scores && scores.total_score > 0;
				const isSelected = selectedFramework?.id === framework.id;
				const isDisabled = !framework.is_enabled;
				const isFirstFramework = sortedFrameworks.indexOf(framework) === 0;
				const isRestrictedForFreeTier = isFreeTier && !isFirstFramework;

				return (
					<div 
						onClick={(e) => {
							e.stopPropagation();
							if (hasScore && !isDisabled && !isRestrictedForFreeTier) {
								handleHistoryOpen(framework.id);
								setHistoryFramework(framework.id);
							}
						}}
						style={{ 
							cursor: (hasScore && !isDisabled && !isRestrictedForFreeTier) ? 'pointer' : 'default',
							opacity: (hasScore && !isDisabled && !isRestrictedForFreeTier) ? 1 : 0.3,
							filter: isSelected ? 'brightness(0) invert(1)' : 'none' // Make icon white when selected
						}}
					>
						<ScoreHistoryIcon />
					</div>
				);
			},
		},
		// Add edit column only for admin users and FMA managers
		...((userData?.is_admin === 1 || userData?.account_type === 5) ? [{
			id: "edit",
			header: () => <span style={headerTextStyle}>Edit</span>,
			width: "5%",
			cell: ({ row }: { row: any }) => {
				const framework = row.original;
				const isDisabled = !framework.is_enabled;
				return (
					<div 
						onClick={(e) => {
							e.stopPropagation();
							handleEditFramework(framework);
						}}
						style={{ 
							// cursor: 'pointer',
							padding: '4px',
							borderRadius: '4px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',

							
								cursor: ( !isDisabled) ? 'pointer' : 'default',
								opacity: (!isDisabled) ? 1 : 0.3,
								// filter: isSelected ? 'brightness(0) invert(1)' : 'none' // Make icon white when selected
						}}
						// onMouseEnter={(e) => {
						// 	e.currentTarget.style.backgroundColor = '#f0f0f0';
						// }}
						// onMouseLeave={(e) => {
						// 	e.currentTarget.style.backgroundColor = 'transparent';
						// }}
					>
						<i className="bi bi-pencil" style={{  fontSize: '16px' }}></i>
					</div>
				);
			},
		}] : [])
	];

	const fetchDomains = async (frameworkId: number) => {
		try {
			setIsContentLoading(true);
			const domains = await createAxiosInstance().get(
				framework_domains(frameworkId)
			);
			if (domains.status == 401 || domains.status == 403) {
				handleAuthError();
				return;
			}
			console.log(domains, "domains")
			setDomains(domains?.data);
		} catch (e) {
			console.log(e);
		} finally {
			setIsContentLoading(false);
		}
	};

	// Update the useEffect to only fetch on initial load and userId change
	useEffect(() => {
		if (userId && refresh === 0) { // Only on initial load
			fetchCategoryScore();
		}
	}, [userId]);

	// Set first framework as selected by default and fetch its domains - restore from localStorage
	useEffect(() => {
		if (frameworks && frameworks.length > 0 && !selectedFramework && userId) {
			let frameworkToSelect = sortedFrameworks[0];
			
			// Try to restore from localStorage for this user
			const savedFrameworkId = localStorage.getItem(`maturity_selectedFrameworkId_${userId}`);
			if (savedFrameworkId) {
				const savedFramework = sortedFrameworks.find(f => f.id.toString() === savedFrameworkId);
				if (savedFramework && savedFramework.is_enabled) {
					// For free tier users, only allow first framework
					const isFirstFramework = sortedFrameworks.indexOf(savedFramework) === 0;
					const isRestrictedForFreeTier = isFreeTier && !isFirstFramework;
					
					if (!isRestrictedForFreeTier) {
						frameworkToSelect = savedFramework;
					}
				}
			}
			
			setSelectedFramework(frameworkToSelect);
			fetchDomains(frameworkToSelect.id); // Domains will auto-select first via useEffect
		}
	}, [frameworks, selectedFramework, refresh, userId, isFreeTier]);

	// Domain auto-selection is now handled by the Domains component itself

	const getLatestScores = (
		scores: FlowScore[],
		questionFlows: QuestionFlow[]
	) => {
		const latestScoresMap = new Map<number, FlowScore>();

		// Filter scores to only include those with question_flow_id matching any id in questionFlows
		const validScores = scores.filter((score) =>
			questionFlows.some((flow) => flow.id === score.question_flow_id)
		);

		validScores.map((score) => {
			const existingScore = latestScoresMap.get(score.question_flow_id);
			if (
				!existingScore ||
				new Date(score.score_date) > new Date(existingScore.score_date)
			) {
				latestScoresMap.set(score.question_flow_id, score);
			}
		});

		return Array.from(latestScoresMap.values());
	};

	useEffect(() => {
		const fetchQuestionsFlow = async () => {
			if (!selectedDomain) return;

			try {
				setIsQuestionsLoading(true);
				const questionsFlowResponse = await createAxiosInstance().get(
					question_flows(selectedDomain?.id ?? 0)
				);

				if (
					questionsFlowResponse.status == 401 ||
					questionsFlowResponse.status == 403
				) {
					// 401 is for expired token
					// 403 is for forbidden access (blocked by GPT.)
					console.log("user token expired");
					logout();
					navigate("/");
					return;
				}
				setQuestionFlows(questionsFlowResponse?.data);
				flowScores(questionsFlowResponse?.data);
			} catch (e: any) {
				console.log(e);
			} finally {
				setIsQuestionsLoading(false);
			}
		};

		const flowScores = async (questionFlows: QuestionFlow[]) => {
			// if (!selectedDomain) return;

			try {
				const response = await createAxiosInstance().get(
					user_flow_scores(userId ? userId : 1)
				);

				if (response.status == 401 || response.status == 403) {
					// 401 is for expired token
					// 403 is for forbidden access (blocked by GPT.)
					console.log("use token expired");

					logout();
					window.location.href = "/";
					return;
				}
				setFlowScores(response.data);

				const latestScores = getLatestScores(response.data, questionFlows);
				setLatestScores(latestScores);
			} catch (e) {
				console.log(e, "Error");
			}
		};

		fetchQuestionsFlow();
		// fetchDomains is already called when domain is selected, no need to call it again here
	}, [selectedDomain, refresh]);

	const handleRefresh = async () => {
		setIsRefreshing(true);
		
		try {
			// Store current framework selection before clearing
			const currentFramework = selectedFramework;
			
			// Clear frameworks to show loading state
			setSelectedFramework(null);
			
			// Step 1: Refresh framework scores (category scores) first
			console.log("Refreshing framework scores...");
			await fetchCategoryScore();
			
			// Step 2: Refresh domains
			if (currentFramework) {
				console.log("Refreshing domains...");
				await fetchDomains(currentFramework.id);
			}
			
			// Step 3: Increment refresh counter to trigger question flow refresh via useEffect
			console.log("Triggering question flow refresh...");
			setRefresh(prev => prev + 1);
			
			// Small delay to ensure all refreshes complete
			await new Promise(resolve => setTimeout(resolve, 500));
			
		} catch (error) {
			console.error("Error during refresh:", error);
		} finally {
			setIsRefreshing(false);
		}
	};

	const refreshFlowScores = async () => {
		if (!userId || !selectedDomain) return;
		
		try {
			setIsQuestionsLoading(true);
			const response = await createAxiosInstance().get(
				user_flow_scores(userId)
			);

			if (response.status == 401 || response.status == 403) {
				console.log("user token expired");
				logout();
				window.location.href = "/";
				return;
			}
			
			setFlowScores(response.data);
			
			// Recalculate latest scores
			if (questionFlows.length > 0) {
				const latestScores = getLatestScores(response.data, questionFlows);
				setLatestScores(latestScores);
			}
			
			console.log("Flow scores refreshed:", response.data);
		} catch (e) {
			console.log(e, "Error refreshing flow scores");
		} finally {
			setIsQuestionsLoading(false);
		}
	};

	const handleQuit = () => {
		navigate("/");
	};

	const handleAddFrameworkSuccess = async (frameworkData?: FrameworkCategory) => {
		if (isEditMode) {
			setAlert({
				message: 'Framework updated successfully!',
				type: 'success',
				show: true
			});
		} else {
			// For new frameworks, show setup sidescreen instead of just alert
			if (frameworkData) {
				setNewFrameworkData(frameworkData);
				setShowFrameworkSetup(true);
			}
			setAlert({
				message: 'Framework created successfully! Complete the setup by adding domains, question flows, and questions.',
				type: 'success',
				show: true
			});
		}
		
		// Refresh the frameworks list from parent component
		if (onFrameworksChange) {
			await onFrameworksChange();
		}
		
		// Refresh internal data (category scores, domains, etc.)
		handleRefresh();
	};

	const handleCloseAddFrameworkModal = () => {
		setShowAddFrameworkModal(false);
		setIsEditMode(false);
		setEditFrameworkData(null);
	};

	const handleEditFramework = (framework: FrameworkCategory) => {
		setIsEditMode(true);
		setEditFrameworkData(framework);
		setShowAddFrameworkModal(true);
	};

	const handleCloseFrameworkSetup = () => {
		setShowFrameworkSetup(false);
		setNewFrameworkData(null);
	};

	const handleEditScoringOptions = (frameworkId: number) => {
		setEditScoringOptionsFrameworkId(frameworkId);
		setShowScoringOptionsEdit(true);
	};

	const handleCloseScoringOptionsEdit = () => {
		setShowScoringOptionsEdit(false);
		setEditScoringOptionsFrameworkId(null);
	};

	const handleExportData = async() => {
		if (!userId) window.location.href = "/";
		try {
			const scoreHistory = await createAxiosInstance().get(
				export_data(userId||0), {
					responseType: "blob", 
				  }
			);

			if (scoreHistory.status == 401 || scoreHistory.status == 403) {
				console.log("user token expired");
				logout();
				return;
			}

			if (scoreHistory.status == 500) {
				console.log("Trouble downloading the file. Please try again later.");
				setAlert({
					message: "Trouble downloading the file. Please try again later.",
					type: "danger",
					show: true,
				});
				return;
			}
			
			const contentDisposition = scoreHistory.headers["content-disposition"];
			let filename = "downloaded_file.xlsx"; // Default filename

			if (contentDisposition) {
				const match = contentDisposition.match(/filename="?(.+?)"?$/);
				filename = match?.[1] || filename;
			}
			const blob = new Blob([scoreHistory.data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			  });

			FileSaver.saveAs(blob, filename.replace(/["']/g, ''));
		} catch (e) {
			console.log(e);
			setAlert({
				message: "Trouble downloading the file. Please try again later.",
				type: "danger",
				show: true,
			});
		}
	};

	return <div className="container-fluid mt-4">
				<div className="row">
					<div className="col-md-12">
						<div className="card text-start" style={cardStyle}>
							<div className="card-header" style={headerStyle}>
								<div className="d-flex justify-content-between align-items-center">
									<h3 className="text-blue" style={{fontSize: '18px', fontWeight: 'bold', marginBottom: 0}}>
										Cybersecurity & Privacy Assessments
									</h3>
									{(userData?.is_admin === 1 || userData?.account_type === 5) && (
										<button
											type="button"
											className="btn btn-rounded-blue d-flex align-items-center gap-2"
											onClick={() => setShowAddFrameworkModal(true)}
											style={{
												width: '15%',
												borderRadius: '77.125rem',
												transition: 'all 0.3s ease',
											}}
											onMouseEnter={(e) => {
												const target = e.target as HTMLButtonElement;
												target.style.backgroundColor = '#26397E';
												target.style.transform = 'translateY(-2px)';
											}}
											onMouseLeave={(e) => {
												const target = e.target as HTMLButtonElement;
												target.style.backgroundColor = '#5376F0';
												target.style.transform = 'translateY(0)';
											}}
										>
											{/* <AddIcon style={{width: '16px', height: '16px', backgroundColor: 'white', borderRadius: '50%', color: '#183FCE'}}/> */}
											ADD NEW FRAMEWORK
										</button>
									)}
								</div>
							</div>
							<div className="card-body" style={{paddingTop: '0px',}}>
								<div className="d-flex flex-row">
									<div style={frameworksListStyle} id="frameworks-list">
									{isLoading ? (
										<div className="d-flex justify-content-center align-items-center" style={{ height: '200px', marginTop: '0.8rem' }}>
											<div className="spinner-border text-primary" role="status">
												<span className="visually-hidden">Loading...</span>
											</div>
										</div>
									) : (
										<AdminTables 
											containerStyle={{
												width: "100%",
												overflowX: "auto",
												marginTop: '0.8rem'
											}} 
											data={sortedFrameworks}
											columns={tableColumns}
											rowSelectionEnabled={true}
											onRowSelect={(framework) => {
												// Only allow selection if framework is enabled and not restricted for free tier
												const isFirstFramework = sortedFrameworks.indexOf(framework) === 0;
												const isRestrictedForFreeTier = isFreeTier && !isFirstFramework;
												
												if (framework.is_enabled && !isRestrictedForFreeTier) {
													handleFrameworkSelect(framework);
												}
											}}
											showSearch={false}
											isLoading={false}
											selectedRowId={selectedFramework?.id}
											selectedRowStyle={{
												backgroundColor: "#5376F0",
												color: "#FFFFFF"
											}}
											isRowDisabled={(framework) => {
												const isFirstFramework = sortedFrameworks.indexOf(framework) === 0;
												const isRestrictedForFreeTier = isFreeTier && !isFirstFramework;
												return !framework.is_enabled || isRestrictedForFreeTier;
											}}
										/>
									)}
									</div>
									<div className="d-flex flex-column" style={{flex: 2}}>
										
										<div className="d-flex flex-row align-items-center gap-3 offcanvas-history-text py-2 px-2" style={{marginLeft: '10px', marginTop: '10px'}}>
											<BulbIcon />
											<p style={{marginBottom: 0, color: "#252525", fontSize: '14px', fontWeight: 'normal'}}>Get latest score by completing the individual assessment</p>
										</div>
										<div className="d-flex flex-row">
																						<div style={sectionStyle}>
												{isContentLoading ? (
													<div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
														<div className="spinner-border text-primary" role="status">
															<span className="visually-hidden">Loading...</span>
														</div>
													</div>
												) : (
													<Domains
														key={`framework-${selectedFramework?.id}`}
														domains={isFreeTier ? 
															domains.map((domain, index) => ({
																...domain,
																is_enabled: index === 0 // Only first domain enabled for free tier
															})) : 
															domains
														}
														handleQuit={() => navigate(-1)}
														frameworkId={selectedFramework?.id || 0}
														selectDomain={(domain: FrameworkDomain) => {
															setSelectedDomain(domain);
														}}
														refresh={refresh}
														frameworkData={selectedFramework}
														onRefreshRequest={handleRefresh}
													/>
												)}
											</div>
											<div style={lastSectionStyle}>
												{isQuestionsLoading ? (
													<div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
														<div className="spinner-border text-primary" role="status">
															<span className="visually-hidden">Loading...</span>
														</div>
													</div>
												) : (
													selectedDomain && (<QuestionsFlow
														frameworkId={Number(selectedFramework?.id)}
														selectedDomain={selectedDomain}
														questionFlows={isFreeTier ? 
															questionFlows.map((flow, index) => ({
																...flow,
																is_enabled: index === 0, // Only first question flow enabled for free tier
																is_free: index === 0 // Override is_free to ensure first item is accessible
															})) : 
															questionFlows
														}
														flowScores={flowScores}
														latestScores={latestScores}
														handleGoBack={() => {
															navigate(-1);
														}}
														handleRefresh={handleRefresh}
														refresh={refresh}
													/>)
												)}
											</div>
										</div>
										
									</div>
									
								</div>
							</div>
						</div>
					</div>
				</div>
				<ScoreHistory
					isOpen={isOpen}
					handleClose={handleHistoryClose}
					loading={historyLoading}
					content={historyContent}
					isFramework={isFramework}
					frameworkId={historyFramework || 0}
				/>
				{showAddFrameworkModal && <AddFrameworkModal
					show={showAddFrameworkModal}
					onClose={handleCloseAddFrameworkModal}
					onSuccess={handleAddFrameworkSuccess}
					isEditMode={isEditMode}
					editData={editFrameworkData}
					onEditScoringOptions={handleEditScoringOptions}
				/>}
				{alert && (
					<Alert
						message={alert.message}
						type={alert.type}
						show={alert.show}
						onClose={() => setAlert(null)}
					/>
				)}
				<FrameworkSetupSidescreen
					isOpen={showFrameworkSetup}
					onClose={handleCloseFrameworkSetup}
					frameworkData={newFrameworkData}
				/>
				<ScoringOptionsEditSidebar
					isOpen={showScoringOptionsEdit}
					onClose={handleCloseScoringOptionsEdit}
					frameworkId={editScoringOptionsFrameworkId || 0}
				/>
				{/* <AddData isSidebarOpen={isAddDataSidebarOpen} setIsSidebarOpen={setIsAddDataSidebarOpen} /> */}
			</div>
};

export default MaturityAssessment;
